import { useState, useEffect } from "react";
import toast from "../../../../utils/toast";
import {
  getSystemSettingData,
  getStateWithApi,
  saveCompanyInfo,
  getSupportContactsList,
  createSupportContactsList,
  updateSupportContactsList,
  deleteSupportContactsList,
  getSystemSettingfeatureToggleList,
  updateSystemSettingFeatureToggle,
} from "../../../../services/admin/adminServices";

export default function SystemSetting() {
  const [systemForm, setSystemForm] = useState({
    projectName: "",
    companyName: "",
    city: "",
    stateId: 0,
    completeAddress: "",
    gstNumber: "",
    cinNumber: "",
    defaultCurrency: 0,
    timezone: 0,
  });
  const [systemSaveLoading, setSystemSaveLoading] = useState(false);
  const [statesList, setStatesList] = useState([]);
  const [supportContacts, setSupportContacts] = useState([]);
  const [supportSaveLoading, setSupportSaveLoading] = useState(false);
  const [originalContacts, setOriginalContacts] = useState([]);
  const [featureToggles, setFeatureToggles] = useState({
    isKYCVerificationModule: false,
    isDigiLockerAutoKYC: false,
    iseSign: false,
    isWhatsAppNotification: false,
    isTenantMobileApp: false,
    isPaymentGateway: false,
    isMaintenanceMode: false,
  });

  useEffect(() => {
    fetchSystemSettings();
    fetchStatesList();
    fetchSupportContacts();
    fetchFeatureToggles();
  }, []);

  const fetchSystemSettings = async () => {
    try {
      const res = await getSystemSettingData();
      if (res.statusCode === 1) {
        const d = res.data;
        setSystemForm({
          projectName: d.projectName || "",
          companyName: d.companyName || "",
          city: d.city || "",
          stateId: d.stateId || 0,
          completeAddress: d.completeAddress || "",
          gstNumber: d.gstNumber || "",
          cinNumber: d.cinNumber || "",
          defaultCurrency: d.defaultCurrency ?? 0,
          timezone: d.timezone ?? 0,
        });
      }
    } catch (err) {
      console.log("Failed to fetch system settings");
    }
  };

  const fetchStatesList = async () => {
    try {
      const res = await getStateWithApi();
      setStatesList(res);
    } catch (err) {
      console.log("Failed to fetch states");
    }
  };

  const handleSystemChange = (e) => {
    const { name, value } = e.target;
    setSystemForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveSystemSettings = async () => {
    setSystemSaveLoading(true);
    try {
      const payload = {
        ...systemForm,
        stateId: Number(systemForm.stateId),
        defaultCurrency: Number(systemForm.defaultCurrency),
        timezone: Number(systemForm.timezone),
      };
      const res = await saveCompanyInfo(payload);
      if (res.statusCode === 1) {
        toast.success(res.message);
        fetchSystemSettings();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setSystemSaveLoading(false);
    }
  };

  const CONTACT_TYPES = ["Email", "Mobile", "Phone", "WhatsApp No", "Telegram"];

  const fetchSupportContacts = async () => {
    try {
      const res = await getSupportContactsList();
      if (res.statusCode === 1) {
        const data = res.data.map((c) => ({ ...c, _id: c.id }));
        setSupportContacts(data);
        setOriginalContacts(data);
      }
    } catch (err) {
      console.log("Failed to fetch support contacts");
    }
  };

  const addSupportRow = () => {
    setSupportContacts((prev) => [
      ...prev,
      {
        _id: Date.now(),
        id: 0,
        contactType: "Email",
        contactName: "",
        contactValue: "",
      },
    ]);
  };

  const updateSupportRow = (index, field, value) => {
    setSupportContacts((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)),
    );
  };

  const removeSupportRow = async (index) => {
    const contact = supportContacts[index];
    if (!contact.id) {
      setSupportContacts((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    try {
      const res = await deleteSupportContactsList(contact.id);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setSupportContacts((prev) => prev.filter((_, i) => i !== index));
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    }
  };

  const handleSaveSupportContacts = async () => {
    const newRows = supportContacts.filter((c) => c.id === 0);

    const changedRows = supportContacts.filter((c) => {
      if (c.id === 0) return false;
      const original = originalContacts.find((o) => o.id === c.id);
      if (!original) return false;
      return (
        original.contactType !== c.contactType ||
        original.contactName !== c.contactName ||
        original.contactValue !== c.contactValue
      );
    });

    if (newRows.length === 0 && changedRows.length === 0) {
      toast.info("No changes to save.");
      return;
    }

    setSupportSaveLoading(true);
    try {
      for (const row of newRows) {
        if (!row.contactName || !row.contactValue) continue;
        const res = await createSupportContactsList({
          contactType: row.contactType,
          contactName: row.contactName,
          contactValue: row.contactValue,
        });
        if (res.statusCode === 1) toast.success(res.message);
        else toast.error(res.message);
      }

      for (const row of changedRows) {
        const res = await updateSupportContactsList(row.id, {
          contactType: row.contactType,
          contactName: row.contactName,
          contactValue: row.contactValue,
        });
        if (res.statusCode === 1) toast.success(res.message);
        else toast.error(res.message);
      }

      fetchSupportContacts();
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setSupportSaveLoading(false);
    }
  };

  const fetchFeatureToggles = async () => {
    try {
      const res = await getSystemSettingfeatureToggleList();
      if (res.statusCode === 1) setFeatureToggles(res.data);
    } catch (err) {
      console.log("Failed to fetch feature toggles");
    }
  };

  const handleToggleChange = async (key) => {
    const updated = { ...featureToggles, [key]: !featureToggles[key] };
    setFeatureToggles(updated);
    try {
      const res = await updateSystemSettingFeatureToggle(updated);
      if (res.statusCode === 1) {
        toast.success(res.message);
      } else {
        toast.error(res.message);

        setFeatureToggles(featureToggles);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
      setFeatureToggles(featureToggles);
    }
  };
  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title" id="page-title">
              System-Settings
            </div>
            <div className="page-sub" id="page-sub" />
          </div>
          <div className="topbar-right" id="topbar-right" />
        </div>
        <div className="content" id="content-area">
          <div className="card mb16">
            <div className="card-title">Project &amp; Company Information</div>
            <div className="form-grid">
              <div className="form-group">
                <label>Project Name *</label>
                <input
                  type="text"
                  name="projectName"
                  value={systemForm.projectName}
                  onChange={handleSystemChange}
                  placeholder="e.g. NestManager"
                />
              </div>
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  name="companyName"
                  value={systemForm.companyName}
                  onChange={handleSystemChange}
                  placeholder="e.g. MyRentMitra Technologies Pvt Ltd"
                />
              </div>
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  name="city"
                  value={systemForm.city}
                  onChange={handleSystemChange}
                  placeholder="e.g. Bengaluru"
                />
              </div>
              <div className="form-group">
                <label>State</label>
                <select
                  name="stateId"
                  value={systemForm.stateId}
                  onChange={handleSystemChange}
                >
                  <option value={0}>Select State</option>
                  {statesList.map((s) => (
                    <option key={s.stateId} value={s.stateId}>
                      {s.stateName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group full">
                <label>Complete Address</label>
                <textarea
                  name="completeAddress"
                  value={systemForm.completeAddress}
                  onChange={handleSystemChange}
                  style={{ minHeight: 50 }}
                  placeholder="Full company address"
                />
              </div>
              <div className="form-group">
                <label>GST Number</label>
                <input
                  type="text"
                  name="gstNumber"
                  value={systemForm.gstNumber}
                  onChange={handleSystemChange}
                  placeholder="e.g. 29AAGCN1234F1ZX"
                />
              </div>
              <div className="form-group">
                <label>CIN Number</label>
                <input
                  type="text"
                  name="cinNumber"
                  value={systemForm.cinNumber}
                  onChange={handleSystemChange}
                  placeholder="e.g. U72900KA2023PTC170001"
                />
              </div>
              <div className="form-group">
                <label>Default Currency</label>
                <select
                  name="defaultCurrency"
                  value={systemForm.defaultCurrency}
                  onChange={handleSystemChange}
                >
                  <option value={0}>INR (₹)</option>
                  <option value={1}>USD ($)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Timezone</label>
                <select
                  name="timezone"
                  value={systemForm.timezone}
                  onChange={handleSystemChange}
                >
                  <option value={0}>Asia/Kolkata (IST +5:30)</option>
                  <option value={1}>UTC</option>
                </select>
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <button
                className="btn btn-primary"
                onClick={handleSaveSystemSettings}
                disabled={systemSaveLoading}
              >
                {systemSaveLoading ? "Saving..." : "Save Company Info"}
              </button>
            </div>
          </div>
          <div className="card mb16">
            <div className="card-title">Support Contact Details</div>
            <div className="info-box">
              Add multiple contact points for each communication type. These
              appear in the platform's help and contact sections.
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                marginBottom: 12,
              }}
            >
              {supportContacts.map((contact, index) => (
                <div
                  key={contact.id || contact._id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "160px 1fr 1fr 36px",
                    gap: 8,
                    alignItems: "center",
                    background: "var(--bg-secondary)",
                    padding: "10px 12px",
                    borderRadius: "var(--radius-md)",
                    border: "0.5px solid var(--border-light)",
                  }}
                >
                  <select
                    value={contact.contactType}
                    onChange={(e) =>
                      updateSupportRow(index, "contactType", e.target.value)
                    }
                    style={{ fontSize: 12, padding: "6px 8px" }}
                  >
                    {CONTACT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>

                  <input
                    type="text"
                    value={contact.contactName}
                    onChange={(e) =>
                      updateSupportRow(index, "contactName", e.target.value)
                    }
                    placeholder="Dept / Contact name"
                    style={{ fontSize: 12 }}
                  />

                  <input
                    type="text"
                    value={contact.contactValue}
                    onChange={(e) =>
                      updateSupportRow(index, "contactValue", e.target.value)
                    }
                    placeholder="Email, phone, username…"
                    style={{ fontSize: 12 }}
                  />

                  <button
                    onClick={() => removeSupportRow(index)}
                    style={{
                      width: 32,
                      height: 32,
                      border: "0.5px solid #F09595",
                      background: "var(--red-light)",
                      color: "var(--red)",
                      borderRadius: 6,
                      cursor: "pointer",
                      fontSize: 16,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: "inherit",
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}

              {supportContacts.length === 0 && (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--text-tertiary)",
                    padding: "10px 0",
                  }}
                >
                  No contacts added. Click "+ Add Contact" to add.
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn" onClick={addSupportRow}>
                + Add Contact
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveSupportContacts}
                disabled={supportSaveLoading}
              >
                {supportSaveLoading ? "Saving..." : "Save Contacts"}
              </button>
            </div>
          </div>
          <div className="card">
            <div className="card-title">Feature Toggles</div>

            {[
              {
                key: "isKYCVerificationModule",
                label: "KYC Verification Module",
                desc: "Enable/disable KYC for all owners",
              },
              {
                key: "isDigiLockerAutoKYC",
                label: "DigiLocker Auto KYC",
                desc: "Aadhaar-based instant verification",
              },
              {
                key: "iseSign",
                label: "Digital Agreements (eSign)",
                desc: "Electronic agreement signing",
              },
              {
                key: "isWhatsAppNotification",
                label: "WhatsApp Notifications",
                desc: "Platform-wide WhatsApp alerts",
              },
              {
                key: "isTenantMobileApp",
                label: "Tenant Mobile App",
                desc: "Tenant-facing mobile access",
              },
              {
                key: "isPaymentGateway",
                label: "Online Payment Gateway",
                desc: "Razorpay/Cashfree integration",
              },
              {
                key: "isMaintenanceMode",
                label: "Maintenance Mode",
                desc: "Takes platform offline for all users",
              },
            ].map(({ key, label, desc }) => (
              <div className="setting-row" key={key}>
                <div>
                  <div className="setting-label">{label}</div>
                  <div className="setting-desc">{desc}</div>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={featureToggles[key] || false}
                    onChange={() => handleToggleChange(key)}
                  />
                  <span className="toggle-slider" />
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
